/**
 * MOCKUP — Create DO from Plan (feedback chị Đẹp, 2026-06-04)
 * ----------------------------------------------------------------------------
 * KHÔNG phải tính năng live. Đây là bản mockup tương tác để chị Đẹp click thử
 * và feedback luồng "tạo DO (Delivery Order) từ Plan" TRƯỚC khi code thật +
 * wire backend. Tất cả data là mock inline, mọi Save chỉ hiện snackbar giả lập.
 *
 * Route tạm: /matching/do-mockup  (gỡ route + file này sau khi chốt feedback).
 *
 * Mockup phản ánh 3 điểm feedback:
 *   ① 2 chỗ tạo DO từ Plan:
 *        (a) Khi Save S-Instruction  → pop-up Create DO (To WH = kho TLC: BẢO LỘC / NHƠN TRẠCH)
 *        (b) Khi Save PS Direct match → pop-up Create DO (To WH = Shipper của HĐ P: HƯƠNG BẢN / PHÚC MINH)
 *      Draft table cột: Sale No | To WH (edit) | DO No (book mới, edit) | DO Qty (klg vừa instruct/match, edit) | Note (edit)
 *      Pop-up có [Save] (insert bảng DO) + [Cancel] (bỏ qua, book DO sau).
 *   ② Button "Create DO" ở Data Plan — cho những Plan đã tạo (mở lại pop-up trên).
 *   ③ Khi Edit Plan: show danh sách DO hiện có theo S để cập nhật From WH, NW
 *      tương ứng plan vừa đổi.
 *
 * Style bám convention module matching: accent S #fb8c00, P #1e88e5, factory
 * #43a047; số căn phải #,###; date DD-MMM-YYYY; Autocomplete flat combo.
 */
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import { useMemo, useState } from 'react'

import AddBusinessOutlinedIcon from '@mui/icons-material/AddBusinessOutlined'
import CloseIcon from '@mui/icons-material/Close'
import EditOutlinedIcon from '@mui/icons-material/EditOutlined'
import LocalShippingOutlinedIcon from '@mui/icons-material/LocalShippingOutlined'
import SaveOutlinedIcon from '@mui/icons-material/SaveOutlined'
import StorefrontOutlinedIcon from '@mui/icons-material/StorefrontOutlined'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'

import { useSnackbar } from '../../../hooks/useSnackbar'

/* ---------- accents (đồng bộ matching module) ---------------------------- */
const ACCENT_S = '#fb8c00'
const ACCENT_P = '#1e88e5'
const ACCENT_DO = '#6d4c41' // copper/brown — DO (giao hàng), tách màu với P/S/stock

/* ---------- mock dropdown options ---------------------------------------- */
// Kho TLC (instruct case) + Shipper của HĐ P (direct case) gộp 1 list để combo
// "To WH" cho phép edit tự do giữa 2 nhóm.
const WH_OPTIONS = ['BẢO LỘC', 'NHƠN TRẠCH', 'HƯƠNG BẢN', 'PHÚC MINH', 'TÂN PHÁT']

/* ---------- types --------------------------------------------------------- */
interface DoDraftRow {
  id: string
  saleNo: string // Sale No (read-only — từ S contract vừa instruct/match)
  buyer: string // hiển thị phụ cho dễ nhìn (không insert)
  toWh: string // To WH — edit (kho TLC hoặc Shipper)
  doNo: string // DO No — book mới, edit
  doQty: number // DO Qty — klg vừa instruct/match, edit
  note: string // Note — text
}

type Scenario = 'instruct' | 'direct'

/* ---------- mock seeds cho pop-up ----------------------------------------- */
// Khi Save S-Instruction: To WH mặc định = tên kho TLC vừa chọn (BẢO LỘC).
const SEED_INSTRUCT: DoDraftRow[] = [
  { id: 'i1', saleNo: 'S-2406-018', buyer: 'OLAM', toWh: 'BẢO LỘC', doNo: '', doQty: 19_200, note: '' },
  { id: 'i2', saleNo: 'S-2406-021', buyer: 'SUCAFINA', toWh: 'BẢO LỘC', doNo: '', doQty: 9_600, note: '' },
]
// Khi Save PS Direct: To WH mặc định = Shipper của HĐ P tương ứng.
const SEED_DIRECT: DoDraftRow[] = [
  { id: 'd1', saleNo: 'S-2406-007', buyer: 'NEUMANN', toWh: 'HƯƠNG BẢN', doNo: '', doQty: 28_800, note: '' },
  { id: 'd2', saleNo: 'S-2406-009', buyer: 'LOUIS DREYFUS', toWh: 'PHÚC MINH', doNo: '', doQty: 14_400, note: '' },
]

/* ---------- mock Data Plan rows (đã tạo plan) ----------------------------- */
const DATA_PLAN_ROWS = [
  { saleNo: 'S-2406-018', buyer: 'OLAM', grade: 'R1', matchKg: 19_200, src: 'S-Instruct · BẢO LỘC', hasDo: true },
  { saleNo: 'S-2406-021', buyer: 'SUCAFINA', grade: 'R2', matchKg: 9_600, src: 'S-Instruct · BẢO LỘC', hasDo: false },
  { saleNo: 'S-2406-007', buyer: 'NEUMANN', grade: 'R1', matchKg: 28_800, src: 'Direct · HƯƠNG BẢN', hasDo: false },
  { saleNo: 'S-2406-031', buyer: 'ECOM', grade: 'R1', matchKg: 4_800, src: 'S-Instruct · NHƠN TRẠCH', hasDo: false },
]

/* ---------- mock existing DO theo S (Edit Plan) --------------------------- */
interface ExistingDo {
  id: string
  doNo: string
  fromWh: string // From WH — edit khi plan đổi
  nw: number // NW (net weight) — edit khi plan đổi
  doQty: number
  status: string
}
const EXISTING_DOS_SEED: ExistingDo[] = [
  { id: 'e1', doNo: 'DO-2406-1102', fromWh: 'BẢO LỘC', nw: 12_000, doQty: 12_000, status: 'Booked' },
  { id: 'e2', doNo: 'DO-2406-1119', fromWh: 'BẢO LỘC', nw: 7_200, doQty: 7_200, status: 'Booked' },
]

/* ---------- helpers ------------------------------------------------------- */
const fmtNum = (n: number) => (n ? Math.round(n).toLocaleString('en-US') : '')

/* ======================================================================== */
/*  PAGE                                                                     */
/* ======================================================================== */
export default function DoMockupPage() {
  const snack = useSnackbar()
  const [createOpen, setCreateOpen] = useState(false)
  const [scenario, setScenario] = useState<Scenario>('instruct')
  const [editOpen, setEditOpen] = useState(false)
  const [editSale, setEditSale] = useState<string>('')

  const openCreate = (s: Scenario) => {
    setScenario(s)
    setCreateOpen(true)
  }
  const openEdit = (saleNo: string) => {
    setEditSale(saleNo)
    setEditOpen(true)
  }

  return (
    <Box sx={{ p: { xs: 1.5, sm: 2.5 }, maxWidth: 1200, mx: 'auto' }}>
      {/* Banner: đây là mockup, không phải live */}
      <Alert severity="info" icon={<LocalShippingOutlinedIcon />} sx={{ mb: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          MOCKUP — Create DO from Plan (để review feedback, chưa phải tính năng live)
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Mọi nút Save ở đây chỉ giả lập (hiện thông báo) — không ghi DB. Mục đích để chị Đẹp click thử
          luồng & góp ý trước khi code thật + nối backend.
        </Typography>
      </Alert>

      {/* ── ① 2 chỗ tạo DO ─────────────────────────────────────────────── */}
      <SectionTitle no="①" title="Hai chỗ tạo DO từ Plan" />
      <Stack direction={{ xs: 'column', md: 'row' }} spacing={1.5} sx={{ mb: 3 }}>
        <EntryCard
          accent={ACCENT_S}
          icon={<StorefrontOutlinedIcon />}
          chip="S"
          title="Khi Save S-Instruction"
          desc="Sau khi instruct Sale vào kho TLC và bấm Save Plan → bật pop-up tạo DO. To WH mặc định = tên kho TLC (BẢO LỘC / NHƠN TRẠCH)."
          btnLabel="Giả lập: Save S-Instruction"
          onClick={() => openCreate('instruct')}
        />
        <EntryCard
          accent={ACCENT_P}
          icon={<SwapHorizIcon />}
          chip="P↔S"
          title="Khi Save PS Direct match"
          desc="Sau khi match trực tiếp P↔S và bấm Save Plan → bật pop-up tạo DO. To WH mặc định = Shipper của HĐ P (HƯƠNG BẢN / PHÚC MINH)."
          btnLabel="Giả lập: Save PS Direct"
          onClick={() => openCreate('direct')}
        />
      </Stack>

      {/* ── ② Data Plan + button Create DO ─────────────────────────────── */}
      <SectionTitle
        no="②"
        title="Data Plan — thêm button Create DO cho Plan đã tạo"
      />
      <Paper variant="outlined" sx={{ mb: 3, overflow: 'hidden' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            px: 1.25,
            py: 0.75,
            bgcolor: alpha(ACCENT_DO, 0.06),
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          <Typography variant="subtitle2" sx={{ fontWeight: 800, flex: 1 }}>
            DATA PLAN (mock)
          </Typography>
          <Button
            size="small"
            variant="contained"
            startIcon={<AddBusinessOutlinedIcon />}
            onClick={() => openCreate('instruct')}
            sx={doBtnSx}
          >
            Create DO
          </Button>
        </Box>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ '& th': { fontWeight: 800, fontSize: '0.72rem', whiteSpace: 'nowrap' } }}>
                <TableCell>Sale No</TableCell>
                <TableCell>Buyer</TableCell>
                <TableCell>Grade</TableCell>
                <TableCell align="right">Match KG</TableCell>
                <TableCell>Plan Source</TableCell>
                <TableCell>DO?</TableCell>
                <TableCell align="right">Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {DATA_PLAN_ROWS.map((r) => (
                <TableRow key={r.saleNo} hover sx={{ '& td': { fontSize: '0.72rem' } }}>
                  <TableCell sx={{ fontWeight: 700 }}>{r.saleNo}</TableCell>
                  <TableCell>{r.buyer}</TableCell>
                  <TableCell>{r.grade}</TableCell>
                  <TableCell align="right">{fmtNum(r.matchKg)}</TableCell>
                  <TableCell>{r.src}</TableCell>
                  <TableCell>
                    {r.hasDo ? (
                      <Chip size="small" label="Có DO" color="success" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                    ) : (
                      <Chip size="small" label="Chưa" variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                    )}
                  </TableCell>
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title="Tạo DO cho plan này">
                        <Button
                          size="small"
                          variant="outlined"
                          startIcon={<AddBusinessOutlinedIcon sx={{ fontSize: 15 }} />}
                          onClick={() => openCreate(r.src.startsWith('Direct') ? 'direct' : 'instruct')}
                          sx={{ ...doBtnOutlinedSx }}
                        >
                          Create DO
                        </Button>
                      </Tooltip>
                      <Tooltip title="Edit Plan → xem/sửa DO theo S">
                        <Button
                          size="small"
                          variant="text"
                          startIcon={<EditOutlinedIcon sx={{ fontSize: 15 }} />}
                          onClick={() => openEdit(r.saleNo)}
                          sx={{ textTransform: 'none', fontSize: '0.68rem', minWidth: 0 }}
                        >
                          Edit
                        </Button>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>

      {/* ── ③ Edit Plan → DO list theo S ───────────────────────────────── */}
      <SectionTitle
        no="③"
        title="Edit Plan — show danh sách DO hiện có theo S để cập nhật From WH / NW"
      />
      <Paper variant="outlined" sx={{ p: 1.5 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
          Trong dialog Edit Plan của 1 Sale, thêm 1 vùng liệt kê DO hiện có theo S đó. Khi plan đổi
          (KG, kho…), chị Q cập nhật <b>From WH</b> và <b>NW</b> cho khớp.
        </Typography>
        <Button
          variant="contained"
          startIcon={<EditOutlinedIcon />}
          onClick={() => openEdit('S-2406-018')}
          sx={{ ...doBtnSx, bgcolor: ACCENT_S, '&:hover': { bgcolor: alpha(ACCENT_S, 0.85) } }}
        >
          Giả lập: Edit Plan (S-2406-018)
        </Button>
      </Paper>

      {/* Dialogs */}
      <CreateDoDialog
        open={createOpen}
        scenario={scenario}
        onClose={() => setCreateOpen(false)}
        onSaved={(n) => {
          setCreateOpen(false)
          snack.success(`(Mock) Đã insert ${n} dòng vào bảng DO.`)
        }}
      />
      <EditPlanDosDialog
        open={editOpen}
        saleNo={editSale}
        onClose={() => setEditOpen(false)}
        onSaved={() => {
          setEditOpen(false)
          snack.success('(Mock) Đã cập nhật From WH / NW cho các DO theo S.')
        }}
      />
    </Box>
  )
}

/* ======================================================================== */
/*  ① + ②  CREATE DO POP-UP                                                  */
/* ======================================================================== */
function CreateDoDialog({
  open,
  scenario,
  onClose,
  onSaved,
}: {
  open: boolean
  scenario: Scenario
  onClose: () => void
  onSaved: (rowCount: number) => void
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {open ? <CreateDoBody scenario={scenario} onClose={onClose} onSaved={onSaved} /> : null}
    </Dialog>
  )
}

function CreateDoBody({
  scenario,
  onClose,
  onSaved,
}: {
  scenario: Scenario
  onClose: () => void
  onSaved: (rowCount: number) => void
}) {
  // Mount mỗi lần mở → reset draft từ seed theo scenario.
  const [rows, setRows] = useState<DoDraftRow[]>(() =>
    (scenario === 'direct' ? SEED_DIRECT : SEED_INSTRUCT).map((r, i) => ({
      ...r,
      // Gợi ý DO No "book mới" — số tăng dần (mock). Thật sẽ gọi BE suggest no.
      doNo: `DO-2406-${(1200 + i + 1).toString()}`,
    })),
  )

  const patch = (id: string, key: keyof DoDraftRow, value: string | number) =>
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, [key]: value } : r)))

  const totalQty = useMemo(() => rows.reduce((s, r) => s + (r.doQty || 0), 0), [rows])
  const whHint =
    scenario === 'direct'
      ? 'To WH = Shipper của HĐ P (vd HƯƠNG BẢN, PHÚC MINH) — cho phép edit.'
      : 'To WH = kho TLC vừa instruct (BẢO LỘC / NHƠN TRẠCH) — cho phép edit.'

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pr: 1 }}
      >
        <Chip
          size="small"
          icon={<AddBusinessOutlinedIcon sx={{ fontSize: 15, color: '#fff !important' }} />}
          label="DO"
          sx={{ bgcolor: ACCENT_DO, color: '#fff', fontWeight: 700, height: 22 }}
        />
        <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1 }}>
          Create DO from {scenario === 'direct' ? 'PS Direct match' : 'S-Instruction'}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        <Alert severity="info" sx={{ mb: 1.5, py: 0.25 }}>
          <Typography variant="caption">
            Draft DO vừa được instruct/match. {whHint} <b>DO No</b> book mới, <b>DO Qty</b> = klg vừa
            instruct/match, đều edit được. Bấm <b>Save</b> để ghi bảng DO, hoặc <b>Cancel</b> nếu chưa
            muốn book DO liền.
          </Typography>
        </Alert>

        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    bgcolor: alpha(ACCENT_DO, 0.07),
                  },
                }}
              >
                <TableCell sx={{ width: 130 }}>Sale No</TableCell>
                <TableCell sx={{ width: 180 }}>To WH</TableCell>
                <TableCell sx={{ width: 150 }}>DO No</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>DO Qty</TableCell>
                <TableCell>Note</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {rows.map((r) => (
                <TableRow key={r.id}>
                  {/* Sale No — read-only */}
                  <TableCell sx={{ verticalAlign: 'top', pt: 1.25 }}>
                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '0.74rem' }}>
                      {r.saleNo}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.64rem' }}>
                      {r.buyer}
                    </Typography>
                  </TableCell>
                  {/* To WH — Autocomplete free-solo (chọn list hoặc gõ tay) */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={WH_OPTIONS}
                      value={r.toWh}
                      onChange={(_e, v) => patch(r.id, 'toWh', v ?? '')}
                      onInputChange={(_e, v) => patch(r.id, 'toWh', v)}
                      renderInput={(p) => (
                        <TextField {...p} size="small" sx={cellInputSx} />
                      )}
                    />
                  </TableCell>
                  {/* DO No — book mới, edit */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      value={r.doNo}
                      onChange={(e) => patch(r.id, 'doNo', e.target.value)}
                      sx={cellInputSx}
                    />
                  </TableCell>
                  {/* DO Qty — number, edit */}
                  <TableCell align="right" sx={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      value={r.doQty ? String(r.doQty) : ''}
                      onChange={(e) => {
                        const n = Number(e.target.value.replace(/[^\d]/g, ''))
                        patch(r.id, 'doQty', Number.isFinite(n) ? n : 0)
                      }}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                      sx={cellInputSx}
                    />
                  </TableCell>
                  {/* Note — text */}
                  <TableCell sx={{ verticalAlign: 'top' }}>
                    <TextField
                      size="small"
                      placeholder="Ghi chú…"
                      value={r.note}
                      onChange={(e) => patch(r.id, 'note', e.target.value)}
                      sx={cellInputSx}
                      fullWidth
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" justifyContent="flex-end" sx={{ mt: 1 }}>
          <Typography variant="caption" color="text.secondary">
            {rows.length} DO • Tổng DO Qty: <b>{fmtNum(totalQty)}</b> kg
          </Typography>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 1.5, py: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={() => onSaved(rows.length)}
          sx={doBtnSx}
        >
          Save DO
        </Button>
      </DialogActions>
    </>
  )
}

/* ======================================================================== */
/*  ③  EDIT PLAN — DO list theo S                                            */
/* ======================================================================== */
function EditPlanDosDialog({
  open,
  saleNo,
  onClose,
  onSaved,
}: {
  open: boolean
  saleNo: string
  onClose: () => void
  onSaved: () => void
}) {
  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      {open ? <EditPlanDosBody saleNo={saleNo} onClose={onClose} onSaved={onSaved} /> : null}
    </Dialog>
  )
}

function EditPlanDosBody({
  saleNo,
  onClose,
  onSaved,
}: {
  saleNo: string
  onClose: () => void
  onSaved: () => void
}) {
  const [dos, setDos] = useState<ExistingDo[]>(() => EXISTING_DOS_SEED.map((d) => ({ ...d })))
  const patch = (id: string, key: keyof ExistingDo, value: string | number) =>
    setDos((prev) => prev.map((d) => (d.id === id ? { ...d, [key]: value } : d)))

  return (
    <>
      <DialogTitle
        component="div"
        sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1, pr: 1 }}
      >
        <Chip size="small" label="S" sx={{ bgcolor: ACCENT_S, color: '#fff', fontWeight: 700, height: 22 }} />
        <Typography variant="subtitle1" sx={{ fontWeight: 800, flex: 1 }}>
          Edit Plan — {saleNo}
        </Typography>
        <IconButton size="small" onClick={onClose}>
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers sx={{ p: 1.5 }}>
        {/* (mock) phần edit plan gốc — chỉ gợi ý chỗ đặt */}
        <Paper variant="outlined" sx={{ p: 1.25, mb: 1.5, bgcolor: alpha(ACCENT_S, 0.04) }}>
          <Typography variant="caption" color="text.secondary">
            … (các field Edit Plan hiện có: Match Date, KG, kho instruct… giữ nguyên) …
          </Typography>
        </Paper>

        <Divider sx={{ mb: 1 }}>
          <Chip
            size="small"
            icon={<LocalShippingOutlinedIcon sx={{ fontSize: 15 }} />}
            label="DO hiện có theo S"
            sx={{ bgcolor: alpha(ACCENT_DO, 0.1), fontWeight: 700 }}
          />
        </Divider>

        <Alert severity="warning" sx={{ mb: 1.5, py: 0.25 }}>
          <Typography variant="caption">
            Plan vừa thay đổi → cập nhật <b>From WH</b> và <b>NW</b> các DO bên dưới cho khớp.
          </Typography>
        </Alert>

        <TableContainer sx={{ border: 1, borderColor: 'divider', borderRadius: 1 }}>
          <Table size="small">
            <TableHead>
              <TableRow
                sx={{
                  '& th': {
                    fontWeight: 800,
                    fontSize: '0.7rem',
                    whiteSpace: 'nowrap',
                    bgcolor: alpha(ACCENT_DO, 0.07),
                  },
                }}
              >
                <TableCell sx={{ width: 150 }}>DO No</TableCell>
                <TableCell sx={{ width: 190 }}>From WH</TableCell>
                <TableCell align="right" sx={{ width: 130 }}>NW</TableCell>
                <TableCell align="right" sx={{ width: 120 }}>DO Qty</TableCell>
                <TableCell>Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {dos.map((d) => (
                <TableRow key={d.id}>
                  <TableCell sx={{ fontWeight: 700, fontSize: '0.74rem' }}>{d.doNo}</TableCell>
                  <TableCell>
                    <Autocomplete
                      freeSolo
                      size="small"
                      options={WH_OPTIONS}
                      value={d.fromWh}
                      onChange={(_e, v) => patch(d.id, 'fromWh', v ?? '')}
                      onInputChange={(_e, v) => patch(d.id, 'fromWh', v)}
                      renderInput={(p) => <TextField {...p} size="small" sx={cellInputSx} />}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <TextField
                      size="small"
                      value={d.nw ? String(d.nw) : ''}
                      onChange={(e) => {
                        const n = Number(e.target.value.replace(/[^\d]/g, ''))
                        patch(d.id, 'nw', Number.isFinite(n) ? n : 0)
                      }}
                      inputProps={{ inputMode: 'numeric', style: { textAlign: 'right' } }}
                      sx={cellInputSx}
                    />
                  </TableCell>
                  <TableCell align="right" sx={{ fontSize: '0.74rem' }}>{fmtNum(d.doQty)}</TableCell>
                  <TableCell>
                    <Chip size="small" label={d.status} variant="outlined" sx={{ height: 18, fontSize: '0.62rem' }} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </DialogContent>

      <DialogActions sx={{ px: 1.5, py: 1 }}>
        <Button onClick={onClose} color="inherit">
          Cancel
        </Button>
        <Button
          variant="contained"
          startIcon={<SaveOutlinedIcon />}
          onClick={onSaved}
          sx={{ ...doBtnSx, bgcolor: ACCENT_S, '&:hover': { bgcolor: alpha(ACCENT_S, 0.85) } }}
        >
          Save Plan + DO
        </Button>
      </DialogActions>
    </>
  )
}

/* ======================================================================== */
/*  small presentational helpers                                             */
/* ======================================================================== */
function SectionTitle({ no, title }: { no: string; title: string }) {
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
      <Box
        sx={{
          width: 26,
          height: 26,
          borderRadius: '50%',
          bgcolor: ACCENT_DO,
          color: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
          fontSize: '0.8rem',
          flexShrink: 0,
        }}
      >
        {no}
      </Box>
      <Typography variant="h6" sx={{ fontWeight: 800, fontSize: '1.02rem' }}>
        {title}
      </Typography>
    </Stack>
  )
}

function EntryCard({
  accent,
  icon,
  chip,
  title,
  desc,
  btnLabel,
  onClick,
}: {
  accent: string
  icon: React.ReactNode
  chip: string
  title: string
  desc: string
  btnLabel: string
  onClick: () => void
}) {
  return (
    <Paper
      variant="outlined"
      sx={{
        flex: 1,
        p: 1.5,
        borderLeftWidth: 4,
        borderLeftColor: accent,
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
      }}
    >
      <Stack direction="row" spacing={1} alignItems="center">
        <Chip
          size="small"
          label={chip}
          sx={{ bgcolor: accent, color: '#fff', fontWeight: 700, height: 20 }}
        />
        <Typography variant="subtitle2" sx={{ fontWeight: 800 }}>
          {title}
        </Typography>
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ flex: 1 }}>
        {desc}
      </Typography>
      <Button
        variant="contained"
        startIcon={icon}
        onClick={onClick}
        sx={{
          alignSelf: 'flex-start',
          textTransform: 'none',
          fontWeight: 700,
          bgcolor: accent,
          '&:hover': { bgcolor: alpha(accent, 0.85) },
        }}
      >
        {btnLabel}
      </Button>
    </Paper>
  )
}

/* ---------- shared sx ----------------------------------------------------- */
const doBtnSx = {
  textTransform: 'none',
  fontWeight: 700,
  bgcolor: ACCENT_DO,
  color: '#fff',
  '&:hover': { bgcolor: alpha(ACCENT_DO, 0.85) },
} as const

const doBtnOutlinedSx = {
  textTransform: 'none',
  fontWeight: 700,
  fontSize: '0.68rem',
  minWidth: 0,
  color: ACCENT_DO,
  borderColor: alpha(ACCENT_DO, 0.5),
  '&:hover': { borderColor: ACCENT_DO, bgcolor: alpha(ACCENT_DO, 0.06) },
} as const

const cellInputSx = {
  '& .MuiInputBase-input': { fontSize: '0.72rem', py: 0.5 },
} as const
